import { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, Layers, Sparkles, Brain, Cloud, ShieldCheck, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';

const navigation = [
  { name: 'Home', href: '/' },
  { 
    name: 'Programs', 
    href: '/programs',
    children: [
      { label: 'ERP Systems', icon: Layers, href: '/programs',
        description: '5 enterprise ERP training tracks',
        items: [
          { name: 'All ERP Programs', href: '/programs' },
          { name: 'IFS', href: '/programs/ifs' },
          { name: 'Odoo', href: '/programs/odoo' },
          { name: 'Oracle', href: '/programs/oracle' },
          { name: 'NetSuite', href: '/programs/netsuite' },
          { name: 'Infor', href: '/programs/infor' },
        ]},
      { label: 'Microsoft Track', icon: Sparkles, href: '/microsoft-track', badge: 'New',
        description: '7-cert roadmap · 1750+ questions',
        items: [
          { name: '7-Cert Roadmap', href: '/microsoft-track', badge: 'New' },
          { name: 'AI-900 Exam Prep', href: '/ai-900-exam-prep' },
          { name: 'AZ-900 Exam Prep', href: '/az-900-exam-prep' },
          { name: 'AB-900 Exam Prep', href: '/ab-900-exam-prep' },
          { name: 'SC-900 Exam Prep', href: '/sc-900-exam-prep', badge: 'New' },
          { name: 'AI-102 Exam Prep', href: '/ai-102-exam-prep' },
          { name: 'DP-100 Exam Prep', href: '/dp-100-exam-prep' },
          { name: 'SC-300 Exam Prep', href: '/sc-300-exam-prep' },
          { name: 'AZ-104 Exam Prep', href: '/az-104-exam-prep', badge: 'New' },
        ]},
      { label: 'Google Track', icon: Sparkles, href: '/google-track', badge: 'New',
        description: 'AI-fluency certs for every professional',
        items: [
          { name: 'Google AI Roadmap', href: '/google-track', badge: 'New' },
          { name: 'Google AI Essentials', href: '/google-ai-essentials-exam-prep', badge: 'New' },
        ]},
      { label: 'AWS Track', icon: Sparkles, href: '/aws-track', badge: 'Soon',
        description: '7-cert AWS roadmap · 2026 rollout',
        items: [
          { name: 'AWS 7-Cert Roadmap', href: '/aws-track', badge: 'Soon' },
        ]},
      { label: 'AI Technologies', icon: Brain, href: '/ai-900-exam-prep',
        description: 'Microsoft AI/Data + NVIDIA GenAI certs',
        items: [
          { name: 'AI-900 (Azure AI Fundamentals)', href: '/ai-900-exam-prep' },
          { name: 'AI-102 (Azure AI Engineer)', href: '/ai-102-exam-prep' },
          { name: 'DP-100 (Azure Data Scientist)', href: '/dp-100-exam-prep' },
          { name: 'AB-900 (M365 Copilot Admin)', href: '/ab-900-exam-prep' },
          { name: 'NCA-GENL (NVIDIA GenAI LLMs)', href: '/nca-genl-exam-prep', badge: 'New' },
          { name: 'AWS ML Specialty', href: '/ai', badge: 'Soon' },
          { name: 'TensorFlow Developer', href: '/ai', badge: 'Soon' },
        ]},
      { label: 'Cloud & DevOps', icon: Cloud, href: '/az-900-exam-prep',
        description: 'Azure fundamentals + AWS path',
        items: [
          { name: 'AZ-900 (Azure Fundamentals)', href: '/az-900-exam-prep' },
          { name: 'AWS Cloud Practitioner', href: '/cloud', badge: 'Soon' },
        ]},
      { label: 'Security & Identity', icon: ShieldCheck, href: '/sc-300-exam-prep',
        description: 'Microsoft Entra ID administration',
        items: [
          { name: 'SC-300 (Identity Admin)', href: '/sc-300-exam-prep' },
        ]},
      { label: 'Business Software', icon: Settings, href: '/business-software',
        description: 'CRM and service management',
        items: [
          { name: 'Salesforce', href: '/business-software', badge: 'Soon' },
          { name: 'ServiceNow', href: '/business-software', badge: 'Soon' },
        ]},
    ]
  },
  { name: 'Training Model', href: '/training-model' },
  { name: 'Certification', href: '/certification' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' },
];

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenCategory, setMobileOpenCategory] = useState(null);
  const location = useLocation();

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    setMobileOpenCategory(null);
  }, []);

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg shadow-sm border-b border-slate-100"
      data-testid="header"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
            <img src="/brand/lq-symbol.png" alt="LearnQuanta" className="w-10 h-10 object-contain" />
            <span className="font-bold text-xl text-slate-900 hidden sm:inline">LearnQuanta</span>
          </Link>

          <div className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              item.children ? (
                <DropdownMenu key={item.name}>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center space-x-1 ${
                        location.pathname.startsWith('/programs') || location.pathname.startsWith('/cloud') || location.pathname.startsWith('/business')
                          ? 'text-teal-700 bg-teal-50'
                          : 'text-slate-700 hover:text-teal-700 hover:bg-teal-50'
                      }`}
                      data-testid="nav-programs"
                    >
                      <span>{item.name}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-96 p-2">
                    {item.children.map((group) => {
                      const GroupIcon = group.icon;
                      return (
                        <DropdownMenuSub key={group.label}>
                          <DropdownMenuSubTrigger className="rounded-md px-2 py-2 cursor-pointer hover:bg-teal-50 focus:bg-teal-50 data-[state=open]:bg-teal-50">
                            <div className="flex items-start gap-3 w-full">
                              {GroupIcon && (
                                <div className="w-9 h-9 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0">
                                  <GroupIcon className="w-4 h-4" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium text-slate-900">{group.label}</span>
                                  {group.badge && (
                                    <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">{group.badge}</span>
                                  )}
                                </div>
                                {group.description && (
                                  <p className="text-xs text-slate-500 mt-0.5 leading-snug whitespace-normal">{group.description}</p>
                                )}
                              </div>
                            </div>
                          </DropdownMenuSubTrigger>
                          <DropdownMenuPortal>
                            <DropdownMenuSubContent className="w-72 p-2 ml-1">
                              {group.href && (
                                <DropdownMenuItem asChild>
                                  <Link to={group.href} className="w-full flex items-center justify-between text-teal-700 font-medium border-b border-slate-100 mb-1 pb-2">
                                    <span>View {group.label} hub</span>
                                    <ChevronRight className="w-3.5 h-3.5" />
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              {group.items.map((child) => (
                                <DropdownMenuItem key={child.name} asChild>
                                  <Link to={child.href} className="w-full flex items-center justify-between" data-testid={`nav-program-item-${child.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                    <span>{child.name}</span>
                                    {child.badge && (
                                      <span className={`text-xs px-2 py-0.5 rounded ${child.badge === 'Soon' ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-700'}`}>{child.badge}</span>
                                    )}
                                  </Link>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuPortal>
                        </DropdownMenuSub>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 text-sm font-medium rounded-lg ${
                    location.pathname === item.href
                      ? 'text-teal-700 bg-teal-50'
                      : 'text-slate-700 hover:text-teal-700 hover:bg-teal-50'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>

          <div className="hidden lg:flex items-center space-x-3">
            <Link to="/my-library">
              <Button variant="ghost" className="text-slate-700 hover:bg-violet-50 hover:text-violet-700 rounded-full" data-testid="my-library-btn">
                My Library
              </Button>
            </Link>
            <Link to="/webinar">
              <Button variant="outline" className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="free-webinar-btn">
                Free Webinar
              </Button>
            </Link>
            <Link to="/pricing">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8" data-testid="get-started-btn">
                Get Started
              </Button>
            </Link>
          </div>

          <button 
            className="lg:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 max-h-[80vh] overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-2">
            {navigation.map((item) => (
              item.children ? (
                <div key={item.name} className="space-y-1">
                  <span className="block px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">{item.name}</span>
                  {item.children.map((group) => {
                    const GroupIcon = group.icon;
                    const isOpen = mobileOpenCategory === group.label;
                    return (
                      <div key={group.label} className="rounded-lg border border-slate-100">
                        <button
                          type="button"
                          onClick={() => setMobileOpenCategory(isOpen ? null : group.label)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg ${isOpen ? 'bg-teal-50 text-teal-700' : 'text-slate-700 hover:bg-slate-50'}`}
                          data-testid={`mobile-category-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                          <span className="flex items-center gap-2">
                            {GroupIcon && <GroupIcon className="w-4 h-4" />}
                            <span className="font-medium">{group.label}</span>
                            {group.badge && (
                              <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">{group.badge}</span>
                            )}
                          </span>
                          <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                        </button>
                        {isOpen && (
                          <div className="pb-2 pl-2 pr-1">
                            {group.href && (
                              <Link
                                to={group.href}
                                onClick={closeMobileMenu}
                                className="block px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-50 rounded"
                              >
                                View {group.label} hub →
                              </Link>
                            )}
                            {group.items.map((child) => (
                              <Link
                                key={child.name}
                                to={child.href}
                                className="flex items-center justify-between px-3 py-1.5 text-sm text-slate-600 hover:bg-teal-50 rounded"
                                onClick={closeMobileMenu}
                              >
                                <span>{child.name}</span>
                                {child.badge && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${child.badge === 'Soon' ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-700'}`}>{child.badge}</span>
                                )}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-4 py-2 text-sm font-medium rounded-lg ${
                    location.pathname === item.href
                      ? 'text-teal-700 bg-teal-50'
                      : 'text-slate-700 hover:bg-teal-50'
                  }`}
                  onClick={closeMobileMenu}
                >
                  {item.name}
                </Link>
              )
            ))}
            <div className="pt-4 space-y-2 border-t border-slate-100">
              <Link to="/webinar" className="block" onClick={closeMobileMenu}>
                <Button variant="outline" className="w-full rounded-full border-slate-300">Free Webinar</Button>
              </Link>
              <Link to="/pricing" className="block" onClick={closeMobileMenu}>
                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
